/**
 * Cabeçalho. Os padrões desenham o cabeçalho de antes dos temas: logo à esquerda,
 * busca no meio, fundo branco e fixo ao rolar.
 *
 * Transparente sobre o banner: só vale quando a primeira seção da página é banner ou
 * carrossel em largura total. Nas demais páginas (checkout, carrinho, produto) e ao
 * rolar, o cabeçalho volta à cor sólida escolhida.
 */
export const THEME_HEADER_FIELDS = {
    layout: {
        type: 'select',
        label: 'Disposição',
        options: [
            { value: 'search-center', label: 'Logo à esquerda e busca no meio' },
            { value: 'menu-inline', label: 'Logo à esquerda e menu ao lado' },
            { value: 'logo-center', label: 'Logo centralizado e menu embaixo' },
            { value: 'split', label: 'Logo no meio, menu à esquerda e ícones à direita' },
            { value: 'drawer', label: 'Só logo e ícones; o menu abre num painel lateral' },
        ],
        default: 'search-center',
    },
    color: {
        type: 'select',
        label: 'Cor do cabeçalho',
        options: [
            { value: 'white', label: 'Branco' },
            { value: 'primary', label: 'Cor principal' },
            { value: 'dark', label: 'Escuro' },
        ],
        default: 'white',
    },
    overHero: {
        group: 'Transparente sobre o banner',
        type: 'toggle',
        label: 'Usar o cabeçalho transparente',
        help: 'Quando a página começa com banner ou carrossel em largura total. Fica sólido ao rolar e nas demais páginas.',
        default: false,
    },
    overHeroText: {
        group: 'Transparente sobre o banner',
        type: 'select',
        label: 'Cor dos itens sobre o banner',
        options: [
            { value: 'light', label: 'Clara (banner escuro)' },
            { value: 'dark', label: 'Escura (banner claro)' },
        ],
        default: 'light',
        visibleWhen: { field: 'overHero', equals: true },
    },
    overHeroGradient: {
        group: 'Transparente sobre o banner',
        type: 'toggle',
        label: 'Degradê no topo da imagem',
        help: 'Garante a leitura dos itens sobre qualquer foto.',
        default: true,
        visibleWhen: { field: 'overHero', equals: true },
    },
    search: {
        type: 'select',
        label: 'Busca',
        options: [
            { value: 'field', label: 'Campo aberto' },
            { value: 'icon', label: 'Ícone que abre o campo' },
        ],
        default: 'field',
    },
    logoHeight: { type: 'range', label: 'Altura do logo', min: 28, max: 72, step: 4, unit: ' px', default: 36 },
    showName: { type: 'toggle', label: 'Mostrar o nome da loja ao lado do logo', default: true },
    sticky: { type: 'toggle', label: 'Fixo no topo ao rolar', default: true },
    categoryMenu: {
        group: 'Menu de categorias',
        type: 'select',
        label: 'Menu de categorias',
        help: 'Nas disposições com categorias no topo. As subcategorias vêm do cadastro de categorias. No cabeçalho com painel lateral, as categorias ficam só no painel.',
        options: [
            { value: 'links', label: 'Links simples, sem subcategorias' },
            { value: 'dropdown-hover', label: 'Lista suspensa ao passar o mouse' },
            { value: 'dropdown-click', label: 'Lista suspensa ao clicar' },
            { value: 'mega', label: 'Mega menu ao passar o mouse' },
            { value: 'flyout', label: 'Botão de departamentos com painel' },
            { value: 'image-strip', label: 'Faixa com a imagem de cada categoria' },
        ],
        default: 'links',
    },
    menuUppercase: {
        group: 'Menu de categorias',
        type: 'toggle',
        label: 'Categorias em maiúsculas',
        default: false,
    },
    flyoutLabel: {
        group: 'Menu de categorias',
        type: 'text',
        label: 'Texto do botão de departamentos',
        maxLength: 30,
        default: 'Todos os departamentos',
        visibleWhen: { field: 'categoryMenu', equals: 'flyout' },
    },
    megaShowCollections: {
        group: 'Menu de categorias',
        type: 'toggle',
        label: 'Coluna de coleções no mega menu',
        help: 'As coleções visíveis da loja, como em Destaques.',
        default: false,
        visibleWhen: { field: 'categoryMenu', equals: 'mega' },
    },
    megaHighlights: {
        group: 'Menu de categorias',
        type: 'list',
        label: 'Imagens de destaque do mega menu',
        help: 'Até 2, na lateral do painel.',
        max: 2,
        visibleWhen: { field: 'categoryMenu', equals: 'mega' },
        of: {
            image: { type: 'image', label: 'Imagem', help: 'Largura recomendada: 400 px.' },
            title: { type: 'text', label: 'Título', maxLength: 40 },
            link: { type: 'link', label: 'Link' },
        },
    },
    announcement: {
        group: 'Barra de aviso',
        type: 'text',
        label: 'Barra de aviso',
        help: 'Faixa acima do cabeçalho (frete grátis, cupom). Vazio: sem barra.',
        maxLength: 120,
    },
    announcementLink: { group: 'Barra de aviso', type: 'link', label: 'Link da barra de aviso' },
    announcementColor: {
        group: 'Barra de aviso',
        type: 'select',
        label: 'Cor da barra de aviso',
        options: [
            { value: 'primary', label: 'Cor principal' },
            { value: 'accent', label: 'Cor de destaque' },
            { value: 'dark', label: 'Escuro' },
        ],
        default: 'primary',
    },
    shortcutsEnabled: {
        group: 'Barra de atalhos',
        type: 'toggle',
        label: 'Mostrar a barra de atalhos',
        help: 'Faixa fina acima do cabeçalho com links rápidos. Item sem link não aparece.',
        default: false,
    },
    shortcutsAlign: {
        group: 'Barra de atalhos',
        type: 'select',
        label: 'Posição dos atalhos',
        options: [
            { value: 'left', label: 'À esquerda' },
            { value: 'center', label: 'No centro' },
            { value: 'right', label: 'À direita' },
        ],
        default: 'right',
        visibleWhen: { field: 'shortcutsEnabled', equals: true },
    },
    shortcuts: {
        group: 'Barra de atalhos',
        type: 'list',
        label: 'Atalhos',
        max: 5,
        visibleWhen: { field: 'shortcutsEnabled', equals: true },
        of: {
            icon: { type: 'icon', label: 'Ícone', default: 'box' },
            label: { type: 'text', label: 'Texto', maxLength: 30 },
            link: { type: 'link', label: 'Link' },
        },
        default: [
            { icon: 'box', label: 'Meus pedidos', link: '/account/orders' },
            { icon: 'chat', label: 'Atendimento', link: '' },
            { icon: 'store', label: 'Lojas físicas', link: '' },
        ],
    },
};
//# sourceMappingURL=header.js.map